import { AnimatePresence, motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

export interface UTMEntry {
  name: string;
  sales: number;
  gross: string;
  gross_raw?: number;
  net: string;
  net_raw?: number;
  ticket: string;
  ticket_raw?: number;
}

export interface UTMGroup {
  key: string;
  label: string;
  description: string;
  total_values?: number;
  entries: UTMEntry[];
}

function UTMEntryRow({ entry, isSelected, onToggle }: { entry: UTMEntry; isSelected: boolean; onToggle: () => void }) {
  const { t } = useTranslation(['reports']);
  return (
    <motion.div
      layout
      whileHover={{ x: 2 }}
      onClick={onToggle}
      className={cn(
        'flex flex-col max-sm:items-start max-sm:gap-2 sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg cursor-pointer transition-colors min-w-0',
        isSelected ? 'bg-primary/10 border border-primary/30' : 'hover:bg-zinc-800/50 border border-transparent',
      )}
    >
      <div className="flex-1 min-w-0 w-full">
        <p className={cn('text-sm font-medium truncate', isSelected ? 'text-primary' : 'text-white')}>{entry.name}</p>
        <p className="text-xs text-zinc-500">Ticket: {entry.ticket} • {t('reports:utmPanel.gross').toLowerCase()} {entry.gross}</p>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0 max-sm:w-full max-sm:justify-start">
        <div className="text-center">
          <p className="text-sm font-semibold text-white">{entry.sales}</p>
          <p className="text-[10px] text-zinc-500">{t('reports:utmPanel.sales_label')}</p>
        </div>
        <div className="text-center min-w-[72px]">
          <p className="text-sm font-semibold text-primary">{entry.net}</p>
          <p className="text-[10px] text-zinc-500">{t('reports:utmPanel.net_label')}</p>
        </div>
      </div>
    </motion.div>
  );
}

function UTMGroupCardSkeleton({ fullWidth = false }: { fullWidth?: boolean }) {
  return (
    <motion.div variants={itemVariants} className={cn(fullWidth && 'col-span-1 lg:col-span-2', 'flex')}>
      <Card className="p-0 overflow-hidden flex flex-col w-full min-w-0 min-h-[280px] max-h-[420px] h-auto sm:h-[420px]">
        <div className="px-4 py-3 border-b border-[#27272a] flex items-center justify-between flex-shrink-0">
          <div className="space-y-1.5 min-w-0">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-44" />
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <div className="p-2 flex-1 space-y-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-lg">
              <div className="flex-1 min-w-0 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center space-y-1">
                  <Skeleton className="h-4 w-6 mx-auto" />
                  <Skeleton className="h-2.5 w-10" />
                </div>
                <div className="text-center space-y-1 min-w-[72px]">
                  <Skeleton className="h-4 w-16 mx-auto" />
                  <Skeleton className="h-2.5 w-10 mx-auto" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </motion.div>
  );
}

interface UTMGroupCardProps {
  group: UTMGroup;
  selectedValues: Set<string>;
  onToggleValue: (k: string, v: string) => void;
  fullWidth?: boolean;
  isLoading?: boolean;
}

export default function UTMGroupCard({ group, selectedValues, onToggleValue, fullWidth = false, isLoading = false }: UTMGroupCardProps) {
  const { t } = useTranslation(['reports']);
  const isEmpty = group.entries.length === 0;
  const totalValues = group.total_values ?? group.entries.length;

  if (isLoading) {
    return <UTMGroupCardSkeleton fullWidth={fullWidth} />;
  }

  return (
    <motion.div variants={itemVariants} className={cn(fullWidth && 'col-span-1 lg:col-span-2', 'flex')}>
      <Card className="p-0 overflow-hidden flex flex-col w-full min-w-0 min-h-[280px] max-h-[420px] h-auto sm:h-[420px]">
        <div className="px-4 py-3 border-b border-[#27272a] flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white">{group.label}</h3>
            <p className="text-xs text-zinc-500">{t(group.description)}</p>
          </div>
          <Badge variant="secondary" className="text-xs">{totalValues} {t('reports:utmPanel.values')}</Badge>
        </div>
        <div className="p-2 flex-1 overflow-y-auto space-y-1">
          <AnimatePresence mode="popLayout">
            {isEmpty ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-10 text-center h-full"
              >
                <Info className="h-8 w-8 text-zinc-600 mb-2" />
                <p className="text-sm text-zinc-500">{t('reports:utmPanel.no_data')}</p>
                <p className="text-xs text-zinc-600 mt-1">{t('reports:utmPanel.no_data_desc')}</p>
              </motion.div>
            ) : (
              group.entries.map((entry) => (
                <UTMEntryRow
                  key={entry.name}
                  entry={entry}
                  isSelected={selectedValues.has(`${group.key}:${entry.name}`)}
                  onToggle={() => onToggleValue(group.key, entry.name)}
                />
              ))
            )}
          </AnimatePresence>
        </div>
      </Card>
    </motion.div>
  );
}

export { UTMGroupCardSkeleton };
